import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import {
  createAccessRecoveryTicket,
  createTicket,
  getMyTickets,
  getTicket,
  getMessages,
  sendMessage,
  uploadAttachment,
  MessageResponse,
  TicketCategory,
  AttachmentResponse,
  SupportTicket,
  TicketStatus,
  downloadAttachmentBlob,
} from '../api/supportApi';
import { clientErrorMessage } from '../utils/clientError';
import { supportProject, supportUrl } from '../config/support';
import { useApp } from '../context/AppContext';

const TICKET_ID_KEY = 'support_ticket_id';
const TICKET_TOKEN_KEY = 'support_ticket_token';
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;
const MAX_VIDEO_SIZE = 50 * 1024 * 1024;

interface TicketForm {
  email: string;
  title: string;
  body: string;
  category: TicketCategory;
}

const EMPTY_FORM: TicketForm = {
  email: '',
  title: '',
  body: '',
  category: 'LoginAccess',
};

const AUTH_CATEGORIES: Array<{ value: TicketCategory; label: string }> = [
  { value: 'FundsNotReceived', label: 'Средства не получены' },
  { value: 'PaymentIssue', label: 'Проблема с оплатой' },
  { value: 'KycVerification', label: 'Верификация / KYC' },
  { value: 'ProfileData', label: 'Данные профиля' },
  { value: 'TechnicalIssue', label: 'Техническая ошибка' },
  { value: 'Suggestion', label: 'Предложение' },
];

const PUBLIC_CATEGORIES: Array<{ value: TicketCategory; label: string }> = [
  { value: 'LoginAccess', label: 'Не могу войти' },
  { value: 'Registration', label: 'Регистрация' },
  { value: 'PasswordRecovery', label: 'Восстановление пароля' },
  { value: 'AccountBlocked', label: 'Аккаунт заблокирован' },
  { value: 'TechnicalIssue', label: 'Техническая ошибка' },
];

const STATUS_LABELS: Record<TicketStatus, string> = {
  Open: 'Открыто',
  InProgress: 'В работе',
  WaitingUser: 'Ожидает ответа',
  Resolved: 'Решено',
  Closed: 'Закрыто',
};

const isTerminalStatus = (status?: TicketStatus) => status === 'Closed' || status === 'Resolved';

function AttachmentPreview({ attachment, token }: { attachment: AttachmentResponse; token: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);

  useEffect(() => {
    let revoked = false;
    let objectUrl: string | null = null;
    downloadAttachmentBlob(attachment.id, token)
      .then(blob => {
        if (revoked) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => setBlobUrl(null));

    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachment.id, token]);

  if (!blobUrl) {
    return <div className="sw-attachment-link">{attachment.fileName}</div>;
  }
  if (attachment.contentType.startsWith('image/')) {
    return (
      <a href={blobUrl} target="_blank" rel="noreferrer">
        <img src={blobUrl} alt={attachment.fileName} className="sw-attachment-image" />
      </a>
    );
  }
  if (attachment.contentType.startsWith('video/')) {
    return <video className="sw-attachment-video" src={blobUrl} controls playsInline preload="metadata" />;
  }
  return (
    <a className="sw-attachment-link" href={blobUrl} target="_blank" rel="noreferrer">
      {attachment.fileName}
    </a>
  );
}

export default function SupportWidget() {
  const { userAuth } = useApp();
  const isAuthenticated = Boolean(userAuth.token);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'form' | 'list' | 'chat'>(() =>
    localStorage.getItem(TICKET_ID_KEY) ? 'chat' : 'form',
  );
  const [form, setForm] = useState<TicketForm>(EMPTY_FORM);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [messages, setMessages] = useState<MessageResponse[]>([]);
  const [inputText, setInputText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const ticketId = useRef(localStorage.getItem(TICKET_ID_KEY) || '');
  const ticketToken = useRef(localStorage.getItem(TICKET_TOKEN_KEY) || '');
  const messageIds = useRef(new Set<string>());
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const signalRJoined = useRef(false);
  const chatInitialized = useRef(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const historyInitialized = useRef(false);

  const currentAccessToken = userAuth.token || ticketToken.current;
  const ticketReadOnly = isTerminalStatus(selectedTicket?.status);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (userAuth.user?.email) {
      setForm(f => ({ ...f, email: userAuth.user!.email }));
    }
  }, [userAuth.user]);

  useEffect(() => {
    setForm(f => {
      const categories = isAuthenticated ? AUTH_CATEGORIES : PUBLIC_CATEGORIES;
      return categories.some(category => category.value === f.category)
        ? f
        : { ...f, category: categories[0].value };
    });
  }, [isAuthenticated]);

  const disconnectSignalR = useCallback(async () => {
    const connection = connectionRef.current;
    connectionRef.current = null;
    signalRJoined.current = false;
    if (!connection) return;
    if (ticketId.current) {
      await connection.invoke('LeaveTicket', ticketId.current).catch(() => {});
    }
    await connection.stop().catch(() => {});
  }, []);

  const connectSignalR = useCallback(async (tid: string, token: string) => {
    if (connectionRef.current) return;

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${supportUrl}/hubs/support?access_token=${encodeURIComponent(token)}&project=${encodeURIComponent(supportProject)}`)
      .withAutomaticReconnect()
      .build();

    conn.on('ReceiveMessage', (msg: MessageResponse) => {
      if (msg.isInternal) return;
      setMessages(prev => {
        if (messageIds.current.has(msg.id)) return prev;
        messageIds.current.add(msg.id);
        return [...prev, msg];
      });
    });

    conn.on('TicketStatusChanged', (payload: { ticketId: string; status: TicketStatus }) => {
      if (payload.ticketId !== tid) return;
      setSelectedTicket(current => current ? { ...current, status: payload.status } : current);
      setTickets(current => current.map(ticket =>
        ticket.id === payload.ticketId ? { ...ticket, status: payload.status } : ticket,
      ));
      if (isTerminalStatus(payload.status)) {
        setSelectedFiles([]);
        setInputText('');
      }
    });

    try {
      await conn.start();
      connectionRef.current = conn;
      await conn.invoke('JoinTicket', tid);
      signalRJoined.current = true;
    } catch {
      // Connection failed — REST-based chat still works
    }
  }, []);

  const loadChat = useCallback(
    async (tid: string, token: string) => {
      setLoading(true);
      setError('');
      try {
        const page = await getMessages(tid, token);
        const visible = page.items.filter(m => !m.isInternal);
        messageIds.current = new Set(visible.map(m => m.id));
        setMessages(visible);
        await connectSignalR(tid, token);
      } catch {
        setError('Не удалось загрузить историю сообщений');
      } finally {
        setLoading(false);
      }
    },
    [connectSignalR],
  );

  useEffect(() => {
    if (view !== 'chat' || !ticketId.current || !currentAccessToken) return;
    if (chatInitialized.current) return;
    chatInitialized.current = true;
    loadChat(ticketId.current, currentAccessToken);
  }, [view, loadChat, currentAccessToken]);

  useEffect(() => {
    if (!open || !isAuthenticated || !userAuth.token) return;
    let cancelled = false;

    getMyTickets(userAuth.token)
      .then(page => {
        if (cancelled) return;
        setTickets(page.items);
        const current = page.items.find(ticket => ticket.id === ticketId.current) ?? null;
        if (current) setSelectedTicket(current);
        if (!historyInitialized.current && !ticketId.current) setView('list');
        historyInitialized.current = true;
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить список обращений');
      });

    return () => { cancelled = true; };
  }, [open, isAuthenticated, userAuth.token]);

  // Polling fallback when SignalR is not connected or not joined
  useEffect(() => {
    if (view !== 'chat') return;
    const interval = setInterval(async () => {
      const connected = connectionRef.current?.state === signalR.HubConnectionState.Connected;
      if (connected && signalRJoined.current) return;
      const token = userAuth.token || ticketToken.current;
      if (!ticketId.current || !token) return;
      try {
        const page = await getMessages(ticketId.current, token);
        const visible = page.items.filter(m => !m.isInternal);
        const fresh = visible.filter(m => !messageIds.current.has(m.id));
        if (fresh.length > 0) {
          fresh.forEach(m => messageIds.current.add(m.id));
          setMessages(prev => [...prev, ...fresh]);
        }
      } catch {}
    }, 7000);
    return () => clearInterval(interval);
  }, [view, userAuth.token]);

  const handleSubmitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const res = isAuthenticated
        ? await createTicket({
            guestEmail: userAuth.user?.email ?? '',
            title: form.title,
            body: form.body,
            category: form.category,
          }, userAuth.token!)
        : await createAccessRecoveryTicket({
            email: form.email,
            title: form.title,
            body: form.body,
            category: form.category,
          });
      ticketId.current = res.ticketId;
      ticketToken.current = res.ticketToken;
      localStorage.setItem(TICKET_ID_KEY, res.ticketId);
      localStorage.setItem(TICKET_TOKEN_KEY, res.ticketToken);
      let createdTicket: SupportTicket;
      try {
        createdTicket = await getTicket(res.ticketId, isAuthenticated ? userAuth.token! : res.ticketToken);
      } catch {
        const now = new Date().toISOString();
        createdTicket = {
          id: res.ticketId,
          projectId: '',
          projectName: supportProject,
          guestEmail: isAuthenticated ? userAuth.user?.email ?? '' : form.email,
          title: form.title,
          status: 'Open',
          priority: 'Medium',
          category: form.category,
          assignedAgentName: null,
          createdAt: now,
          updatedAt: now,
          resolvedAt: null,
        };
      }
      setSelectedTicket(createdTicket);
      setTickets(current => [createdTicket, ...current.filter(ticket => ticket.id !== createdTicket.id)]);
      chatInitialized.current = true;
      setMessages([]);
      messageIds.current = new Set();
      setView('chat');
      await loadChat(res.ticketId, res.ticketToken);
    } catch (err: any) {
      setError(clientErrorMessage(err, 'Не удалось создать обращение. Повторите попытку позже.'));
    } finally {
      setSubmitting(false);
    }
  };

  const refreshMessages = useCallback(async () => {
    const token = userAuth.token || ticketToken.current;
    if (!ticketId.current || !token) return;
    const page = await getMessages(ticketId.current, token);
    const visible = page.items.filter(m => !m.isInternal);
    messageIds.current = new Set(visible.map(m => m.id));
    setMessages(visible);
  }, [userAuth.token]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    setError('');
    const valid: File[] = [];

    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        setError('Only photo and video files are allowed.');
        continue;
      }

      const maxSize = isVideo ? MAX_VIDEO_SIZE : MAX_IMAGE_SIZE;
      if (file.size > maxSize) {
        setError(isVideo ? 'Video must be 50 MB or less.' : 'Photo must be 10 MB or less.');
        continue;
      }

      valid.push(file);
    }

    if (valid.length > 0) {
      setSelectedFiles(prev => [...prev, ...valid].slice(0, 5));
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    const text = inputText.trim();
    const files = selectedFiles;
    if ((!text && files.length === 0) || submitting || ticketReadOnly || !currentAccessToken) return;
    setInputText('');
    setSelectedFiles([]);
    setSubmitting(true);

    let tempId: string | null = null;
    if (text) {
      tempId = `temp-${Date.now()}`;
      const optimistic: MessageResponse = {
        id: tempId,
        ticketId: ticketId.current,
        senderId: null,
        senderName: null,
        isGuest: true,
        body: text,
        isInternal: false,
        createdAt: new Date().toISOString(),
        attachments: [],
      };
      messageIds.current.add(tempId);
      setMessages(prev => [...prev, optimistic]);
    }

    try {
      if (text && tempId) {
        const msg = await sendMessage(ticketId.current, currentAccessToken, text);
        messageIds.current.delete(tempId);
        messageIds.current.add(msg.id);
        setMessages(prev => {
          let hasServerMessage = false;
          const next = prev.flatMap(m => {
            if (m.id === tempId) return [];
            if (m.id === msg.id) {
              if (hasServerMessage) return [];
              hasServerMessage = true;
              return [msg];
            }
            return [m];
          });
          return hasServerMessage ? next : [...next, msg];
        });
      }

      for (const file of files) {
        await uploadAttachment(ticketId.current, currentAccessToken, file);
      }

      if (files.length > 0) {
        await refreshMessages();
      }
    } catch (err: any) {
      if (tempId) {
        messageIds.current.delete(tempId);
        setMessages(prev => prev.filter(m => m.id !== tempId));
      }
      setInputText(text);
      setSelectedFiles(files);
      setError(clientErrorMessage(err, 'Не удалось отправить сообщение. Повторите попытку.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewTicket = () => {
    void disconnectSignalR();
    localStorage.removeItem(TICKET_ID_KEY);
    localStorage.removeItem(TICKET_TOKEN_KEY);
    ticketId.current = '';
    ticketToken.current = '';
    chatInitialized.current = false;
    setMessages([]);
    setSelectedTicket(null);
    messageIds.current = new Set();
    setSelectedFiles([]);
    setForm(EMPTY_FORM);
    setError('');
    setView('form');
  };

  const handleShowTickets = () => {
    void disconnectSignalR();
    chatInitialized.current = false;
    setSelectedFiles([]);
    setInputText('');
    setError('');
    setView('list');
  };

  const handleOpenTicket = async (ticket: SupportTicket) => {
    if (!userAuth.token) return;
    await disconnectSignalR();
    ticketId.current = ticket.id;
    ticketToken.current = '';
    localStorage.setItem(TICKET_ID_KEY, ticket.id);
    localStorage.removeItem(TICKET_TOKEN_KEY);
    setSelectedTicket(ticket);
    setMessages([]);
    setSelectedFiles([]);
    setInputText('');
    setError('');
    messageIds.current = new Set();
    chatInitialized.current = true;
    setView('chat');
    await loadChat(ticket.id, userAuth.token);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) handleSend();
  };

  return (
    <>
      {!open && (
        <button className="sw-fab" onClick={() => setOpen(true)} aria-label="Открыть поддержку">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      {open && (
        <div className="sw-panel">
          <div className="sw-header">
            <div className="sw-header-left">
              <span className="sw-dot" />
              <span className="sw-title">Поддержка</span>
            </div>
            <div className="sw-header-actions">
              {isAuthenticated && view !== 'list' && (
                <button className="sw-btn-ghost" onClick={handleShowTickets} title="Все обращения" aria-label="Все обращения">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                </button>
              )}
              {isAuthenticated && view !== 'form' && (
                <button className="sw-btn-ghost" onClick={handleNewTicket} title="Новое обращение">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                </button>
              )}
              <button className="sw-btn-ghost" onClick={() => setOpen(false)} aria-label="Закрыть">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>

          {error && <div className="sw-error">{error}</div>}

          {view === 'list' && (
            <div className="sw-ticket-list">
              <div className="sw-ticket-list-title">Мои обращения</div>
              {tickets.length === 0 ? (
                <div className="sw-ticket-empty">
                  <span>Обращений пока нет</span>
                  <button className="sw-submit" type="button" onClick={handleNewTicket}>Создать обращение</button>
                </div>
              ) : tickets.map(ticket => (
                <button
                  type="button"
                  key={ticket.id}
                  className="sw-ticket-card"
                  onClick={() => void handleOpenTicket(ticket)}
                >
                  <span className="sw-ticket-card-top">
                    <strong>{ticket.title}</strong>
                    <span className={`sw-ticket-status sw-ticket-status--${ticket.status.toLowerCase()}`}>
                      {STATUS_LABELS[ticket.status]}
                    </span>
                  </span>
                  <span className="sw-ticket-card-date">
                    Обновлено {new Date(ticket.updatedAt).toLocaleDateString('ru')}
                  </span>
                </button>
              ))}
            </div>
          )}

          {view === 'form' && (
            <form className="sw-form" onSubmit={handleSubmitForm}>
              {!isAuthenticated && (
                <div className="sw-auth-gate">
                  <a href="/login" className="sw-login-link">Войти в аккаунт</a>
                  <span>Если не получается войти, оставьте обращение для восстановления доступа.</span>
                </div>
              )}
              {!isAuthenticated && <div className="sw-field">
                <label className="sw-label">Email</label>
                <input
                  className="sw-input"
                  type="email"
                  required
                  placeholder="your@email.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>}
              <div className="sw-field">
                <label className="sw-label">Тема</label>
                <input
                  className="sw-input"
                  required
                  placeholder="Кратко опишите проблему"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                />
              </div>
              <div className="sw-field">
                <label className="sw-label">Описание</label>
                <textarea
                  className="sw-textarea"
                  required
                  placeholder="Подробно опишите ситуацию..."
                  rows={4}
                  value={form.body}
                  onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                />
              </div>
              <div className="sw-field">
                <label className="sw-label">Категория</label>
                <select
                  className="sw-select"
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value as TicketCategory }))}
                >
                  {(isAuthenticated ? AUTH_CATEGORIES : PUBLIC_CATEGORIES).map(category => (
                    <option key={category.value} value={category.value}>{category.label}</option>
                  ))}
                </select>
              </div>
              <button className="sw-submit" type="submit" disabled={submitting}>
                {submitting ? 'Отправка...' : 'Создать обращение'}
              </button>
            </form>
          )}

          {view === 'chat' && (
            <div className="sw-chat">
              {selectedTicket && (
                <div className="sw-chat-ticket-head">
                  <span title={selectedTicket.title}>{selectedTicket.title}</span>
                  <span className={`sw-ticket-status sw-ticket-status--${selectedTicket.status.toLowerCase()}`}>
                    {STATUS_LABELS[selectedTicket.status]}
                  </span>
                </div>
              )}
              <div className="sw-messages">
                {loading && <div className="sw-status-text">Загрузка...</div>}
                {!loading && messages.length === 0 && (
                  <div className="sw-status-text">Обращение создано. Ожидайте ответа оператора.</div>
                )}
                {messages.map(m => (
                  <div key={m.id} className={`sw-msg ${m.isGuest ? 'sw-msg--guest' : 'sw-msg--agent'}`}>
                    {!m.isGuest && (
                      <div className="sw-msg-name">{m.senderName || 'Оператор'}</div>
                    )}
                    {m.body && <div className="sw-msg-body">{m.body}</div>}
                    {m.attachments.length > 0 && (
                      <div className="sw-attachments">
                        {m.attachments.map(attachment => (
                          <AttachmentPreview key={attachment.id} attachment={attachment} token={currentAccessToken} />
                        ))}
                      </div>
                    )}
                    <div className="sw-msg-time">
                      {new Date(m.createdAt).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              {!ticketReadOnly && selectedFiles.length > 0 && (
                <div className="sw-selected-files">
                  {selectedFiles.map((file, index) => (
                    <button type="button" key={`${file.name}-${index}`} className="sw-selected-file" onClick={() => removeSelectedFile(index)}>
                      <span>{file.name}</span>
                      <strong>x</strong>
                    </button>
                  ))}
                </div>
              )}
              {ticketReadOnly ? (
                <div className="sw-ticket-readonly">Обращение закрыто. История доступна только для просмотра.</div>
              ) : <div className="sw-input-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  className="sw-file-input"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  className="sw-attach"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting}
                  aria-label="Attach photo or video"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                  </svg>
                </button>
                <input
                  className="sw-input-chat"
                  placeholder="Введите сообщение..."
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={submitting}
                />
                <button
                  className="sw-send"
                  onClick={handleSend}
                  disabled={submitting || (!inputText.trim() && selectedFiles.length === 0)}
                  aria-label="Отправить"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13" />
                    <polygon points="22 2 15 22 11 13 2 9 22 2" />
                  </svg>
                </button>
              </div>}
            </div>
          )}
        </div>
      )}
    </>
  );
}
